import { action, makeObservable, observable } from 'mobx';
import { ImageTags } from '@/constants/CompressorImageTagData.const';
import { MAX_IMAGE_UPLOAD_COUNT } from '@/comp/Forms/SiteSurveyForm/RoomConfigurationStep/FormComponents/ImageUpload/ImageUpload.consts';

export type Image = {
  id: string;
  tag: ImageTags | null;
  file: File;
  url: string;
};

export type ImageAdd = Omit<Image, 'url' | 'tag'>;

export class ImageUploadStore {
  @observable deviceId: string = null;
  @observable currentStep = 0;
  @observable maxFiles: number = MAX_IMAGE_UPLOAD_COUNT;
  @observable images: Image[] = [];
  @observable noEditPermission = false;

  constructor() {
    makeObservable(this);
  }

  @action setMaxFiles = (maxFiles: number) => {
    this.maxFiles = maxFiles;
  };

  @action setDeviceId = (deviceId: string) => {
    this.deviceId = deviceId;
  };

  @action goToNextStep = () => {
    this.currentStep = this.currentStep + 1;
  };

  @action goToPreviousStep = () => {
    this.currentStep = this.currentStep - 1;
  };

  @action goToFirstStep = () => {
    this.currentStep = 0;
  };

  @action addImages = (imagesToAdd: ImageAdd[]) => {
    const images = imagesToAdd.map(image => ({
      tag: null,
      url: URL.createObjectURL(image.file),
      ...image,
    }));

    this.images.push(...images);
  };

  @action removeImage = (id: string) => {
    this.images = this.images.filter(image => {
      if (image.id !== id) {
        return true;
      }

      URL.revokeObjectURL(image.url);
      return false;
    });
  };

  @action addTag = (id: string, tag?: ImageTags) => {
    const image = this.images.find(image => image.id === id);

    image.tag = tag;
  };

  @action removeTag = (id: string) => {
    const image = this.images.find(image => image.id === id);

    image.tag = null;
  };

  @action reset = () => {
    this.deviceId = null;
    this.maxFiles = MAX_IMAGE_UPLOAD_COUNT;
    this.currentStep = 0;
    this.images.splice(0, this.images.length);
    this.noEditPermission = false;
  };

  @action revokeImages = () => {
    this.images.forEach(({ url }) => URL.revokeObjectURL(url));
  };

  @action setNoEditPermission = (noEditPermission: boolean) => {
    this.noEditPermission = noEditPermission;
  };
}
